import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config.js';
import { toApiError } from '../lib/api-error.js';
import type {
  WorkflowHealth,
  WorkflowPromotionState,
  WorkflowManagementStatus,
} from '@chatsdet/types';
import { isWorkflowEligible } from '@chatsdet/types';
import type { FlowsSearchParams } from '../router.js';
import type { Tag } from './useTags.js';
import type { SeedDataOutput } from './useSeedWorkflowOutputs.js';
import type { WorkflowEnvironment } from '../ui/pages/FlowDesigner/WorkflowEnvironmentSettings.js';

export type WorkflowStatus =
  | 'draft'
  | 'approved'
  | 'generating'
  | 'building'
  | 'deleted'
  | 'diagnostic';
/**
 * Legacy suite-inclusion axis (promotion model — eligibility flag OFF): null = never
 * promoted (no indicator), 'promoted' runs in suites, 'paused' explicitly excluded.
 */
export type WorkflowPromotion = WorkflowPromotionState;
export type WorkflowFlowType = 'standard' | 'setup' | 'seed' | 'teardown';

/**
 * Derived, deterministic suite-inclusion gate: a flow is eligible once it is approved
 * AND has passed at least once. Re-exported from `@chatsdet/types` so components share
 * one implementation. Pausing (`pausedAt`) and `degraded` health are separate exclusions.
 */
export { isWorkflowEligible };

export type WorkflowNodeType =
  | 'login'
  | 'page'
  | 'action'
  | 'assertion'
  | 'setup'
  | 'seed'
  | 'wait';

export interface WorkflowNode {
  id: string;
  workflowId: string;
  nodeType: WorkflowNodeType;
  orderIndex: number;
  configJson?: {
    label?: string;
    description?: string;
    url?: string;
    action?: string;
    expectedText?: string;
    [key: string]: unknown;
  } | null;
}

/**
 * Minimal tag type from the server (matches WorkflowTag from backend)
 */
interface WorkflowTagFromServer {
  id: string;
  name: string;
  color: string;
}

export interface WorkflowListItem {
  id: string;
  orgId: string;
  name: string;
  description?: string | null;
  status: WorkflowStatus;
  health: WorkflowHealth;
  /** Legacy promotion axis (eligibility flag OFF): null = never promoted, promoted runs in suites. */
  promotion?: WorkflowPromotion;
  flowType: WorkflowFlowType;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  snapshotVersion: number;
  publishedAt?: string | null;
  approvedAt?: string | null;
  approvedBy?: string | null;
  /** Authoritative "has ever passed" — with `status` derives eligibility. ISO string or null. */
  firstPassedAt?: string | null;
  /** Manual pause toggle (set = paused, excluded from suite runs). ISO string or null. */
  pausedAt?: string | null;
  /** Manual lifecycle axis — distinct from `status` (approval) and `health` (computed). */
  managementStatus?: WorkflowManagementStatus;
  teardownWorkflowId?: string | null;
  teardownSnapshotVersion?: number | null;
  teardownEnabled?: boolean;
  runAgentically?: boolean;
  /** Seed flows only: forces suite scope on every consuming flow's seed node. */
  seedRunOncePerSuite?: boolean;
  /** Seed flows only: declared outputs included in the workflow list payload. */
  seedOutputsJson?: SeedDataOutput[] | null;
  /** Set only while a cache-convergence run parked on this workflow awaits a feedback answer. */
  pendingFeedbackRequestId?: string | null;
  /** Whether the viewer has starred this workflow. */
  isFavorite?: boolean;
}

/**
 * Workflow list item from the paginated API (includes tags from server)
 */
interface WorkflowListItemFromServer extends WorkflowListItem {
  tags: WorkflowTagFromServer[];
}

/**
 * Extended workflow item with derived properties for display
 * Note: nodesPreview is now fetched on-demand when row is expanded
 */
export interface WorkflowListItemWithDetails extends WorkflowListItem {
  tags: Tag[];
}

type WorkflowsPaginatedResponse = {
  ok: boolean;
  data: WorkflowListItemFromServer[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

type TagsListResponse = {
  ok: boolean;
  data: Tag[];
};

type WorkflowsQueryResult = {
  items: WorkflowListItemWithDetails[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

/**
 * Fetch workflows with server-side filtering and pagination.
 * Tags are included in the response. Node previews are fetched on-demand.
 * Pass `enabled: false` while the grouped view is active to avoid a parallel
 * paginated fetch.
 */
export function useWorkflowsQuery(params: FlowsSearchParams, enabled = true) {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 25;

  // Build query key that includes all filter params
  const queryKey = [
    'workflows',
    'paginated',
    {
      page,
      pageSize,
      search: params.search,
      status: params.status,
      health: params.health,
      promotion: params.promotion,
      eligible: params.eligible,
      paused: params.paused,
      lastRunStatus: params.lastRunStatus,
      tagIds: params.tagIds,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      flowType: params.flowType,
      createdByIds: params.createdByIds,
      favorites: params.favorites,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
    },
  ];

  const workflowsQuery = useQuery<WorkflowsPaginatedResponse>({
    queryKey,
    enabled,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set('page', String(page));
      searchParams.set('pageSize', String(pageSize));

      if (params.search) {
        searchParams.set('search', params.search);
      }
      if (params.status && params.status.length > 0) {
        searchParams.set('status', params.status.join(','));
      }
      if (params.health && params.health.length > 0) {
        searchParams.set('health', params.health.join(','));
      }
      if (params.promotion && params.promotion.length > 0) {
        searchParams.set('promotion', params.promotion.join(','));
      }
      if (typeof params.eligible === 'boolean') {
        searchParams.set('eligible', String(params.eligible));
      }
      if (typeof params.paused === 'boolean') {
        searchParams.set('paused', String(params.paused));
      }
      if (params.lastRunStatus && params.lastRunStatus.length > 0) {
        searchParams.set('lastRunStatus', params.lastRunStatus.join(','));
      }
      if (params.tagIds && params.tagIds.length > 0) {
        searchParams.set('tagIds', params.tagIds.join(','));
      }
      if (params.dateFrom) {
        searchParams.set('dateFrom', params.dateFrom);
      }
      if (params.dateTo) {
        searchParams.set('dateTo', params.dateTo);
      }
      if (params.flowType && params.flowType.length > 0) {
        searchParams.set('flowType', params.flowType.join(','));
      }
      if (params.createdByIds && params.createdByIds.length > 0) {
        searchParams.set('createdByIds', params.createdByIds.join(','));
      }
      if (params.favorites) {
        searchParams.set('favoritesOnly', 'true');
      }
      if (params.sortBy) {
        searchParams.set('sortBy', params.sortBy);
      }
      if (params.sortOrder) {
        searchParams.set('sortOrder', params.sortOrder);
      }

      const res = await fetch(`${API_URL}/workflows?${searchParams.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load workflows');
      return res.json();
    },
  });

  // Transform server response to display format
  const result = React.useMemo<WorkflowsQueryResult>(() => {
    if (!workflowsQuery.data) {
      return {
        items: [],
        pagination: { page, pageSize, totalItems: 0, totalPages: 0 },
      };
    }

    const items: WorkflowListItemWithDetails[] = workflowsQuery.data.data.map((w) => ({
      ...w,
      // Convert WorkflowTagFromServer to Tag format (they have same structure)
      tags: w.tags.map((t) => ({
        id: t.id,
        name: t.name,
        color: t.color as Tag['color'],
        orgId: w.orgId,
        description: null,
        createdAt: '',
        updatedAt: '',
        createdBy: null,
      })),
    }));

    return {
      items,
      pagination: workflowsQuery.data.pagination,
    };
  }, [workflowsQuery.data, page, pageSize]);

  return {
    data: result,
    isLoading: workflowsQuery.isLoading,
    isFetching: workflowsQuery.isFetching,
    isError: workflowsQuery.isError,
    error: workflowsQuery.error,
    refetch: workflowsQuery.refetch,
  };
}

/**
 * Skinny flow item served by GET /workflows?view=grouped — only what
 * the grouped section cards render. Dates arrive as ISO strings.
 */
export interface GroupedWorkflowItem {
  id: string;
  name: string;
  status: WorkflowStatus;
  health: WorkflowHealth;
  /** Legacy promotion axis (eligibility flag OFF): null = never promoted, promoted runs in suites. */
  promotion?: WorkflowPromotion;
  flowType: WorkflowFlowType;
  createdAt: string;
  createdBy: string | null;
  /** Authoritative "has ever passed" — with `status` derives eligibility. ISO string or null. */
  firstPassedAt?: string | null;
  /** Manual pause toggle (set = paused). ISO string or null. */
  pausedAt?: string | null;
  /** Manual lifecycle axis — distinct from `status` (approval) and `health` (computed). */
  managementStatus?: WorkflowManagementStatus;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  /** Duration of the last run in ms (finished − started); null while running or never run. */
  lastRunDurationMs: number | null;
  tags: WorkflowTagFromServer[];
  isFavorite: boolean;
}

type GroupedWorkflowsResponse = {
  ok: boolean;
  data: GroupedWorkflowItem[];
  /** True when the server's safety ceiling truncated the result set. */
  capped: boolean;
  pagination: null;
};

/**
 * Fetch ALL flows matching the filters for the grouped-by-tag view (no
 * pagination — a flow must appear under every one of its sections, which
 * doesn't compose with paging). Only enabled while the grouped view is active.
 */
export function useGroupedWorkflowsQuery(params: FlowsSearchParams, enabled: boolean) {
  const queryKey = [
    'workflows',
    'grouped',
    {
      search: params.search,
      status: params.status,
      health: params.health,
      promotion: params.promotion,
      eligible: params.eligible,
      paused: params.paused,
      lastRunStatus: params.lastRunStatus,
      tagIds: params.tagIds,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      flowType: params.flowType,
      createdByIds: params.createdByIds,
      favorites: params.favorites,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
    },
  ];

  return useQuery<GroupedWorkflowsResponse>({
    queryKey,
    enabled,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set('view', 'grouped');

      if (params.search) {
        searchParams.set('search', params.search);
      }
      if (params.status && params.status.length > 0) {
        searchParams.set('status', params.status.join(','));
      }
      if (params.health && params.health.length > 0) {
        searchParams.set('health', params.health.join(','));
      }
      if (params.promotion && params.promotion.length > 0) {
        searchParams.set('promotion', params.promotion.join(','));
      }
      if (typeof params.eligible === 'boolean') {
        searchParams.set('eligible', String(params.eligible));
      }
      if (typeof params.paused === 'boolean') {
        searchParams.set('paused', String(params.paused));
      }
      if (params.lastRunStatus && params.lastRunStatus.length > 0) {
        searchParams.set('lastRunStatus', params.lastRunStatus.join(','));
      }
      if (params.tagIds && params.tagIds.length > 0) {
        searchParams.set('tagIds', params.tagIds.join(','));
      }
      if (params.dateFrom) {
        searchParams.set('dateFrom', params.dateFrom);
      }
      if (params.dateTo) {
        searchParams.set('dateTo', params.dateTo);
      }
      if (params.flowType && params.flowType.length > 0) {
        searchParams.set('flowType', params.flowType.join(','));
      }
      if (params.createdByIds && params.createdByIds.length > 0) {
        searchParams.set('createdByIds', params.createdByIds.join(','));
      }
      if (params.favorites) {
        searchParams.set('favoritesOnly', 'true');
      }
      if (params.sortBy) {
        searchParams.set('sortBy', params.sortBy);
      }
      if (params.sortOrder) {
        searchParams.set('sortOrder', params.sortOrder);
      }

      const res = await fetch(`${API_URL}/workflows?${searchParams.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load workflows');
      return res.json();
    },
  });
}

/**
 * Fetch a single workflow with full details (nodes, edges) for the step preview.
 * Called on-demand when a row is expanded.
 */
export function useWorkflowDetail(workflowId: string | null) {
  return useQuery({
    queryKey: ['workflow-detail', workflowId],
    queryFn: async () => {
      if (!workflowId) return null;
      const res = await fetch(`${API_URL}/workflows/${workflowId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load workflow detail');
      const data = await res.json();
      return data.workflow as WorkflowListItem & {
        nodes: WorkflowNode[];
        edges: Array<{ id: string; sourceNodeId: string; targetNodeId: string }>;
        runAgentically?: boolean;
        /** `merge` or `deploy:<stage>`: when a proven flow is promoted. */
        promoteOn: string;
        /** Suite ids the flow is placed in on promotion; empty means default suite only. */
        targetSuites: string[];
        /** Named environments on the flow's resolved property, when known. */
        environments?: WorkflowEnvironment[];
      };
    },
    enabled: !!workflowId,
    staleTime: 60000, // Cache for 1 minute
  });
}

/**
 * Check if the organization has any workflow that "run all eligible" would execute
 * (eligible + not paused). Useful for determining if Test Runs can be started.
 * Uses a dedicated lightweight endpoint (single EXISTS query).
 */
export function useHasEligibleWorkflows() {
  const query = useQuery<{ ok: boolean; hasEligible: boolean }>({
    queryKey: ['workflows', 'has-eligible'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/workflows/has-eligible`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to check eligible workflows');
      return res.json();
    },
  });

  return {
    hasEligibleWorkflows: query.data?.hasEligible ?? false,
    isLoading: query.isLoading,
  };
}

/** One recent run on an eligible-workflow summary, newest-first (matches the
 *  suite-detail SuiteMemberRun shape so the builder renders the same timeline). */
export interface EligibleWorkflowRun {
  id: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  errorMessage: string | null;
}

/** An eligible workflow summary; `tags` is present only when requested via
 *  `withTags`, and `recentRuns`/`lastRunAt` only when requested via `withRuns`. */
export interface EligibleWorkflowSummary {
  id: string;
  name: string;
  /** standard | setup | seed | teardown | eval — distinguishes auto-run scripts. */
  flowType?: string;
  /** Flow health — `degraded` flows are skipped by run-all/by-tag runs. */
  health?: WorkflowHealth;
  tags?: Array<{ id: string; name: string; color: string }>;
  /** Up to 10 most-recent workflow runs, newest-first (with `withRuns`). */
  recentRuns?: EligibleWorkflowRun[];
  /** Timestamp of the most-recent run, or null if never run (with `withRuns`). */
  lastRunAt?: string | null;
}

/**
 * Fetch minimal eligible (approved + ever-passed, not paused) workflow summaries
 * (id + name) — the flows "run all eligible" would execute. Pass
 * `{ withTags: true }` to also receive each workflow's tags, and/or
 * `{ withRuns: true }` to receive each workflow's recent run history +
 * `lastRunAt` (both used by the suite builder's add palette so freshly-added
 * cards show real metadata immediately) — omitted by default to keep the
 * payload small for other callers (e.g. live run summary display).
 */
export function useEligibleWorkflowSummaries(
  enabled = true,
  options?: { withTags?: boolean; withRuns?: boolean }
) {
  const withTags = options?.withTags ?? false;
  const withRuns = options?.withRuns ?? false;
  return useQuery<{ ok: boolean; workflows: EligibleWorkflowSummary[] }>({
    queryKey: ['workflows', 'eligible-summary', { withTags, withRuns }],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (withTags) params.set('withTags', '1');
      if (withRuns) params.set('withRuns', '1');
      const qs = params.toString();
      const res = await fetch(
        `${API_URL}/workflows/eligible/summary${qs ? `?${qs}` : ''}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to load eligible workflow summaries');
      return res.json();
    },
  });
}

/**
 * Get all available tags for filter options
 */
export function useWorkflowTagOptions() {
  const tagsQuery = useQuery<TagsListResponse>({
    queryKey: ['tags', 'list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/org/tags`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load tags');
      return res.json();
    },
  });

  const options = React.useMemo(() => {
    const tags = tagsQuery.data?.data ?? [];
    return tags.map((t) => ({
      id: t.id,
      label: t.name,
      color: t.color,
    }));
  }, [tagsQuery.data]);

  return {
    options,
    isLoading: tagsQuery.isLoading,
    tags: tagsQuery.data?.data ?? [],
    tagById: React.useMemo(() => {
      const map = new Map<string, Tag>();
      for (const tag of tagsQuery.data?.data ?? []) {
        map.set(tag.id, tag);
      }
      return map;
    }, [tagsQuery.data]),
  };
}

/**
 * Fetch presigned download URL for the original CSV import source file.
 * Returns { ok: false } for 404s (workflow not created from CSV import).
 */
export function useWorkflowImportDownloadUrl(workflowId: string, enabled = true) {
  return useQuery<{
    ok: boolean;
    downloadUrl?: string;
    filename?: string;
    sizeBytes?: number | null;
  }>({
    queryKey: ['workflow-import-download', workflowId],
    enabled,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await fetch(`${API_URL}/workflows/${workflowId}/import/download-url`, {
        credentials: 'include',
      });
      if (res.status === 404) return { ok: false };
      if (!res.ok) throw await toApiError(res, 'Failed to fetch import download URL');
      return res.json();
    },
  });
}

/**
 * Reset all agent-derived data for a workflow (cached actions, agent notes, playbook steps).
 * Forces the next run to do a clean full agent execution.
 */
export function useResetWorkflow(workflowId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!workflowId) throw new Error('No workflow ID');
      const res = await fetch(`${API_URL}/workflows/${workflowId}/reset`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to reset workflow');
      return res.json() as Promise<{ ok: boolean; nodesReset: number }>;
    },
    onSuccess: () => {
      if (workflowId) {
        queryClient.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      }
    },
  });
}
